import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'

interface ComplianceRadialScoreProps {
  score: number
  label?: string
  subtitle?: string
  size?: 'sm' | 'md' | 'lg'
  showWeightedLabel?: boolean
}

/**
 * Editorial radial score gauge for compliance / Guardian Score surfaces.
 *
 * White-card friendly: no glow, no drop-shadow. Severity arc colors map to the
 * editorial palette (green / sand / red); trail is a soft rule line. Center
 * numeral renders in the score's own ink color so the verdict reads at a
 * glance without a colored chip.
 *
 * Editorial severity tokens are kept local — the dark-theme `getScoreColors`
 * in `themeColors.ts` and `complianceColors.ts` both bake in glow/text colors
 * that only read on a dark surface.
 */

const TONE = {
  green: { arc: '#4A9B6E', ink: '#2F6B4A' },
  sand:  { arc: '#C9AD80', ink: '#8A6B3A' },
  red:   { arc: '#C84B4B', ink: '#A03434' },
} as const

function getTier(score: number): { arc: string; ink: string; label: string } {
  if (score >= 90) return { ...TONE.green, label: 'Excellent' }
  if (score >= 80) return { ...TONE.green, label: 'Strong' }
  if (score >= 70) return { ...TONE.sand,  label: 'Good' }
  if (score >= 60) return { ...TONE.sand,  label: 'Fair' }
  if (score >= 40) return { ...TONE.red,   label: 'Needs attention' }
  return { ...TONE.red, label: 'Critical' }
}

const SIZE_CONFIG = {
  sm: { container: 'w-24 h-24', text: 'text-2xl', label: 'text-[8px]' },
  md: { container: 'w-36 h-36', text: 'text-4xl', label: 'text-[10px]' },
  lg: { container: 'w-48 h-48', text: 'text-5xl', label: 'text-[10px]' },
} as const

export default function ComplianceRadialScore({
  score,
  label = 'Compliance Score',
  subtitle,
  size = 'lg',
  showWeightedLabel = false,
}: ComplianceRadialScoreProps) {
  const roundedScore = Math.round(score)
  const tier = getTier(roundedScore)
  const config = SIZE_CONFIG[size]

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${config.container}`}>
        <CircularProgressbar
          value={roundedScore}
          styles={buildStyles({
            pathColor: tier.arc,
            trailColor: '#F0EBE0',
            pathTransitionDuration: 0.8,
            strokeLinecap: 'butt',
          })}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={`font-display ${config.text} font-extrabold tracking-tight tabular-nums`}
            style={{ color: tier.ink }}
          >
            {roundedScore}
          </span>
          <span
            className={`${config.label} font-bold uppercase tracking-[0.16em] mt-1 text-[color:var(--color-mute,#6B7280)]`}
          >
            {label}
          </span>
        </div>
      </div>

      <p
        className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em]"
        style={{ color: tier.ink }}
      >
        {tier.label}
      </p>

      {(subtitle || showWeightedLabel) && (
        <p className="mt-2 text-[12px] italic text-center text-[color:var(--color-mute,#6B7280)]">
          {subtitle || 'Weighted by framework selection'}
        </p>
      )}
    </div>
  )
}
